// @ts-strict-ignore
import * as React from 'react';
import Upload from 'antd/lib/upload';
import Button from 'antd/lib/button';
import Text from 'antd/lib/typography/Text';
import type { UploadFile } from 'antd/lib/upload/interface';
import { PictureOutlined, CloseCircleFilled } from '@ant-design/icons';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { globalMessage } from '../../utils/globalMessage';
import { compressImage } from '../../utils/compressImage';

export const DEFAULT_MAX_IMAGES = 10;

interface ChecklistItemImageUploadProps {
    images: UploadFile[];
    setImages: React.Dispatch<React.SetStateAction<UploadFile[]>>;
    maxImages?: number;
    disabled?: boolean;
}

const ChecklistItemImageUpload: React.FC<ChecklistItemImageUploadProps> = ({
    images,
    setImages,
    maxImages = DEFAULT_MAX_IMAGES,
    disabled = false,
}) => {
    const atLimit = images.length >= maxImages;

    return (
        <div>
            <Upload
                accept="image/*"
                multiple
                showUploadList={false}
                beforeUpload={async (file) => {
                    if (images.length >= maxImages) {
                        globalMessage.warning(`You can upload up to ${maxImages} images.`);
                        return false;
                    }
                    const compressed = await compressImage(file);
                    const reader = new FileReader();
                    reader.onload = () => {
                        const dataUrl = reader.result as string;
                        setImages((prev) => {
                            if (prev.length >= maxImages) return prev;
                            const uploadFile: UploadFile = {
                                uid: `${file.uid}`,
                                name: compressed.name,
                                size: compressed.size,
                                type: compressed.type,
                                originFileObj: compressed as UploadFile['originFileObj'],
                                url: dataUrl,
                            };
                            return [...prev, uploadFile];
                        });
                    };
                    reader.readAsDataURL(compressed);
                    return false;
                }}
                disabled={disabled || atLimit}
            >
                <Button icon={<PictureOutlined />} disabled={disabled || atLimit}>
                    Upload image
                </Button>
            </Upload>
            <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                {images.length}/{maxImages}
            </Text>
            {images.length > 0 && (
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 8,
                        marginTop: 8,
                    }}
                >
                    {images.map((img) => (
                        <div
                            key={img.uid}
                            style={{
                                position: 'relative',
                                width: 72,
                                height: 72,
                                borderRadius: 4,
                                overflow: 'hidden',
                                border: `1px solid ${antGrey.grey4}`,
                            }}
                        >
                            <img
                                src={img.url}
                                alt={img.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            {!disabled && (
                                <CloseCircleFilled
                                    onClick={() => setImages((prev) => prev.filter((i) => i.uid !== img.uid))}
                                    style={{
                                        position: 'absolute',
                                        top: 2,
                                        right: 2,
                                        fontSize: 16,
                                        color: runwayColors.black,
                                        background: runwayColors.white,
                                        borderRadius: '50%',
                                        cursor: 'pointer',
                                    }}
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ChecklistItemImageUpload;
